import type {
  TAnalysisTypeStatus,
  TTaskSourceType,
  TTaskStatus
} from '@homepal/api-contract'
import { ReactNode } from 'react'

import { TDashboardActivityPeriod } from 'components_new/organisms/dialogs/ActivityDialog/utils'

import { ApiKeyType } from 'types/GlobalApiKey'
import { GeneralConfigAttribute } from 'types/GlobalConfig'
import { PeriodType } from 'types/GlobalCustomer'
import { Category } from 'types/GlobalDashboardGroup'
import {
  ComparativeDisplayType,
  Condition,
  RollingType
} from 'types/GlobalKpiOption'
import { MetricStatus, ShowPerEnum } from 'types/GlobalMetric'
import { FlawType } from 'types/GlobalSimFlaw'
import { SnackbarLinkText, SnackbarMessage } from 'types/GlobalSnackbar'
import { EAccountRole } from 'types/GlobalAccount'
import {
  BreakdownByDisplayType,
  ComparativePeriod,
  DateFilterType,
  ForecastPeriod,
  KeyFigureVisualizationType,
  PeriodFilter,
  PieChartValueVisualizationType,
  TextAlign,
  TextVerticalAlign,
  WidgetType
} from 'types/GlobalWidget'
import { Color } from 'themes'
import { Severity } from 'utils/types'
import { EAnalysisStatus, EPriority, EPublicationStatus } from 'types/Enums'
import { IconName } from 'components_new/atoms/Icon'

export const enums = {
  sv: {
    priority: {
      [EPriority.HIGH]: {
        iconName: 'High',
        title: 'Hög'
      },
      [EPriority.MEDIUM]: {
        iconName: 'Medium',
        title: 'Medium'
      },
      [EPriority.LOW]: {
        iconName: 'Low',
        title: 'Låg'
      }
    },
    report_status: {
      [EPublicationStatus.DRAFT]: 'Utkast',
      [EPublicationStatus.PUBLISHED]: 'Publicerad'
    },
    analysis_status: {
      [EAnalysisStatus.UNHANDLED]: 'Ej hanterad',
      [EAnalysisStatus.ACCEPTED]: 'Accepterad',
      [EAnalysisStatus.REJECTED]: 'Avvisad'
    },
    source_types: {
      DASHBOARD: 'Dashboard',
      GLOBAL: 'Global',
      KEY_METRIC: 'Nyckeltal',
      REPORT_EDITION: 'Rapportutgåva',
      ANALYSIS: 'Analys'
    },
    forecast_periods: {
      [ForecastPeriod.FILL_CURRENT_WEEK]: { title: 'Resten av veckan' },
      [ForecastPeriod.FILL_CURRENT_MONTH]: { title: 'Resten av månaden' },
      [ForecastPeriod.FILL_CURRENT_QUARTER]: { title: 'Resten av kvartalet' },
      [ForecastPeriod.FILL_CURRENT_YEAR]: { title: 'Resten av året' },
      [ForecastPeriod.FILL_CURRENT_YEAR_AND_NEXT_YEAR]: {
        title: 'Resten av året och nästa år'
      },
      [ForecastPeriod.FILL_1_MONTH_AHEAD]: {
        title: 'Till och med nästa månad'
      },
      [ForecastPeriod.FILL_3_MONTH_AHEAD]: {
        title: 'Till och med 3 månader framåt'
      },
      [ForecastPeriod.FILL_6_MONTH_AHEAD]: {
        title: 'Till och med 6 månader framåt'
      },
      [ForecastPeriod.FILL_12_MONTH_AHEAD]: {
        title: 'Till och med 12 månader framåt'
      },
      [ForecastPeriod.FILL_3_YEARS_AHEAD]: {
        title: 'Till och med 3 år framåt'
      },
      [ForecastPeriod.FILL_5_YEARS_AHEAD]: {
        title: 'Till och med 5 år framåt'
      },
      [ForecastPeriod.FILL_10_YEARS_AHEAD]: {
        title: 'Till och med 10 år framåt'
      },
      [ForecastPeriod.FILL_25_YEARS_AHEAD]: {
        title: 'Till och med 25 år framåt'
      },
      [ForecastPeriod.FILL_50_YEARS_AHEAD]: {
        title: 'Till och med 50 år framåt'
      }
    },
    flaw_types: {
      [FlawType.DUPLICATE]: { title: 'Duplicerad' },
      [FlawType.IS_NULL]: { title: 'Saknar värde' }
    },
    analysis_type_status: {
      ACTIVE: {
        color: 'success',
        pluralTitle: 'Aktiverade',
        title: 'Aktiverad'
      },
      INACTIVE: {
        color: undefined,
        pluralTitle: 'Inaktiverade',
        title: 'Inaktiverad'
      },
      MISSING_CONFIGURATION: {
        color: undefined,
        pluralTitle: 'Konfiguration saknas',
        title: 'Konfiguration saknas'
      }
    },
    task_status: {
      DONE: { color: 'success', iconName: 'Done', title: 'Klar' },
      IN_PROGRESS: {
        color: 'info',
        iconName: 'InProgress',
        title: 'Pågående'
      },
      TODO: {
        color: undefined,
        iconName: 'Todo',
        title: 'Att göra'
      }
    },
    general_config_attributes: {
      [GeneralConfigAttribute.NumberOfDaysForNewlyRentedObjects]: {
        title:
          'Antalet dagar ett objekt betraktas som nyuthyrt sedan avtalsstart'
      },
      [GeneralConfigAttribute.ObjectRowTypes]: {
        title: 'Debiteringstyper på objekt'
      }
    },
    period_types: {
      [PeriodType.QUARTERLY]: 'Kvartalsvis',
      [PeriodType.MONTHLY]: 'Månadsvis',
      [PeriodType.MANUALLY]: 'Manuellt'
    },
    show_pers: {
      [ShowPerEnum.RENTABLE_SQM]: {
        abbreviation: 'UHYB',
        helperText: 'Fördelat på varje uthyrningsbar m².',
        title: 'Uthyrningsbar m²'
      },
      [ShowPerEnum.ATEMP]: {
        abbreviation: 'ATEMP',
        helperText: 'Fördelat på varje tempererad m² (10 °C).',
        title: 'Tempererad m² (10 °C)'
      },
      [ShowPerEnum.BTA]: {
        abbreviation: 'BTA',
        helperText: 'Fördelat på varje bruttoarea m².',
        title: 'Bruttoarea m²'
      },
      [ShowPerEnum.NUMBER_OF_OBJECTS]: {
        abbreviation: 'HOBJ',
        helperText: 'Fördelat på varje hyresobjekt.',
        title: 'Hyresobjekt'
      },
      [ShowPerEnum.NUMBER_OF_DWELLINGS]: {
        abbreviation: 'BOS',
        helperText: 'Fördelat på varje bostad.',
        title: 'Bostad'
      },
      [ShowPerEnum.NUMBER_OF_SPACES]: {
        abbreviation: 'RUM',
        helperText: 'Fördelat på varje rum.',
        title: 'Rum'
      },
      [ShowPerEnum.NUMBER_OF_UNITS]: {
        abbreviation: 'ENH',
        helperText: 'Fördelat på varje enhet.',
        title: 'Enheter'
      },
      [ShowPerEnum.RENTED_SQM]: {
        abbreviation: 'UHYD',
        helperText: 'Fördelat på varje uthyrd m².',
        title: 'Uthyrd m²'
      },
      [ShowPerEnum.NUMBER_OF_DAYS]: {
        abbreviation: 'DAG',
        helperText: 'Fördelat på varje dag.',
        title: 'Dagar'
      },
      [ShowPerEnum.NUMBER_OF_WORKDAYS]: {
        abbreviation: 'A.DAG',
        helperText: 'Fördelat på varje arbetsdag.',
        title: 'Arbetsdagar'
      },
      [ShowPerEnum.NUMBER_OF_MOVE_INS]: {
        abbreviation: 'INFL',
        helperText: 'Fördelat på varje inflyttning.',
        title: 'Inflyttning'
      },
      [ShowPerEnum.NUMBER_OF_SNOW_DAYS]: {
        abbreviation: 'SNÖD',
        helperText: 'Fördelat på varje dag med snö vid närmsta SMHI-station.',
        title: 'Snödagar'
      }
    },
    group_by_types: {
      SEGMENT_BY: { title: 'Visa per' },
      BREAKDOWN_BY: { title: 'Dela upp efter' }
    },
    period_filters: {
      [PeriodFilter.TODAY]: { title: 'Idag', short: 'Idag' },
      [PeriodFilter.YESTERDAY]: { title: 'Igår', short: 'Igår' },
      [PeriodFilter.THIS_WEEK_TO_TODAY]: {
        title: 'Hittills den här veckan',
        short: 'v'
      },
      [PeriodFilter.THIS_MONTH_TO_TODAY]: {
        title: 'Hittills den här månaden',
        short: 'm'
      },
      [PeriodFilter.THIS_QUARTER_TO_TODAY]: {
        title: 'Hittills det här kvartalet',
        short: 'q'
      },
      [PeriodFilter.THIS_YEAR_TO_TODAY]: {
        title: 'Hittills det här året',
        short: 'å'
      },
      [PeriodFilter.LAST_WEEK]: { title: 'Förra veckan', short: 'fg. v' },
      [PeriodFilter.LAST_MONTH]: { title: 'Förra månaden', short: 'fg. m' },
      [PeriodFilter.LAST_QUARTER]: {
        title: 'Förra kvartalet',
        short: 'fg. q'
      },
      [PeriodFilter.LAST_YEAR]: { title: 'Förra året', short: 'fg. år' },
      [PeriodFilter.LAST_7_DAYS]: {
        title: 'Senaste 7 dagarna',
        short: '7d'
      },
      [PeriodFilter.LAST_30_DAYS]: {
        title: 'Senaste 30 dagarna',
        short: '30d'
      },
      [PeriodFilter.LAST_90_DAYS]: {
        title: 'Senaste 90 dagarna',
        short: '90d'
      },
      [PeriodFilter.LAST_365_DAYS]: {
        title: 'Senaste 365 dagarna',
        short: '365d'
      },
      [PeriodFilter.LATEST_MONTH_WITH_DATA]: {
        title: 'Senaste månaden med data',
        short: '1m m. data'
      },
      [PeriodFilter.LAST_3_MONTHS_TO_TODAY]: {
        title: 'Hittills de senaste 3 månaderna',
        short: '3m.'
      },
      [PeriodFilter.LAST_6_MONTHS_TO_TODAY]: {
        title: 'Hittills de senaste 6 månaderna',
        short: '6m'
      },
      [PeriodFilter.LAST_12_MONTHS_TO_TODAY]: {
        title: 'Hittills de senaste 12 månaderna',
        short: '12m'
      },
      [PeriodFilter.LAST_24_MONTHS_TO_TODAY]: {
        title: 'Hittills de senaste 24 månaderna',
        short: '24m'
      },
      [PeriodFilter.LAST_36_MONTHS_TO_TODAY]: {
        title: 'Hittills de senaste 36 månaderna',
        short: '36m'
      },
      [PeriodFilter.LAST_2_YEARS_TO_TODAY]: {
        title: 'Hittills de senaste 2 åren',
        short: '2å'
      },
      [PeriodFilter.LAST_3_YEARS_TO_TODAY]: {
        title: 'Hittills de senaste 3 åren',
        short: '3å'
      },
      [PeriodFilter.LAST_4_QUARTERS_TO_TODAY]: {
        title: 'Hittills de senaste 4 kvartalen',
        short: '4q'
      },
      [PeriodFilter.LAST_8_QUARTERS_TO_TODAY]: {
        title: 'Hittills de senaste 8 kvartalen',
        short: '8q'
      },
      [PeriodFilter.LAST_12_QUARTERS_TO_TODAY]: {
        title: 'Hittills de senaste 12 kvartalen',
        short: '12q'
      },
      [PeriodFilter.CURRENT_FINANCIAL_YEAR]: {
        title: 'Nuvarande verksamhetsår',
        short: '1vå'
      },
      [PeriodFilter.PREVIOUS_FINANCIAL_YEAR]: {
        title: 'Föregående verksamhetsår',
        short: 'fg. vå'
      },
      [PeriodFilter.LAST_3_FINANCIAL_YEARS_TO_TODAY]: {
        title: 'Hittills de senaste 3 verksamhetsåren',
        short: '3vå'
      }
    },
    conditions: {
      [Condition.IS_NULL]: 'är tom',
      [Condition.EQ]: 'är lika med',
      [Condition.NE]: 'är inte lika med',
      [Condition.LTE]: 'är mindre eller lika med',
      [Condition.LT]: 'är mindre än',
      [Condition.GTE]: 'är större eller lika med',
      [Condition.GT]: 'är större än',
      [Condition.IS_NOT_NULL]: 'är inte tom'
    },
    key_figure_visualization_types: {
      [KeyFigureVisualizationType.FOCUS]: 'Fokus',
      [KeyFigureVisualizationType.LIST]: 'Lista'
    },
    pie_chart_value_visualization_types: {
      [PieChartValueVisualizationType.VALUES]: 'Värden',
      [PieChartValueVisualizationType.PERCENTAGE]: 'Procent',
      [PieChartValueVisualizationType.VALUES_AND_PERCENTAGE]:
        'Värden och procent'
    },
    text_aligns: {
      [TextAlign.LEFT]: 'Vänster',
      [TextAlign.CENTER]: 'Centrerat',
      [TextAlign.RIGHT]: 'Höger'
    },
    text_vertical_aligns: {
      [TextVerticalAlign.TOP]: 'Toppen',
      [TextVerticalAlign.CENTER]: 'Mitten',
      [TextVerticalAlign.BOTTOM]: 'Botten'
    },
    categories: {
      [Category.ENERGY]: 'Hållbarhet',
      [Category.RENTAL]: 'Uthyrning',
      [Category.REAL_ESTATE_PORTFOLIO]: 'Bestånd',
      [Category.TECHNICAL_MANAGEMENT]: 'Förvaltning',
      [Category.ECONOMY]: 'Ekonomi',
      [Category.HOMEPAL]: 'Homepal'
    },
    comparative_display_types: {
      [ComparativeDisplayType.PERCENTAGE]: {
        title: 'Förändring',
        tooltip: 'Visa endast förändring'
      },
      [ComparativeDisplayType.VALUE]: {
        title: 'Värden',
        tooltip: 'Visa endast värden'
      },
      [ComparativeDisplayType.PERCENTAGE_AND_VALUE]: {
        title: 'Båda',
        tooltip: 'Visa både värde och förändring'
      },
      [ComparativeDisplayType.PREVIOUS_VALUE]: {
        title: 'Föregående',
        tooltip: 'Visa endast föregående periods värde'
      },
      [ComparativeDisplayType.HIDDEN]: {
        title: 'Ingen',
        tooltip: 'Dölj förändring'
      }
    },
    widget_types: {
      [WidgetType.KEY_FIGURE]: {
        disabledTitle: 'Detta mätetal går inte att visa som siffra',
        title: 'Siffra',
        summary: 'Visa ett nyckeltal som en ensam siffra.'
      },
      [WidgetType.BAR_CHART]: {
        disabledTitle: 'Detta mätetal går inte att visa som staplar',
        title: 'Staplar',
        summary: 'Visualisera data som stapeldiagram.'
      },
      [WidgetType.BUBBLE_CHART]: {
        disabledTitle: 'Detta mätetal går inte att visa som ett bubbeldiagram',
        title: 'Bubblor',
        summary: 'Jämför datapunkter i ett bubbeldiagram.'
      },
      [WidgetType.LINE_CHART]: {
        disabledTitle: 'Detta mätetal går inte att visa över tid',
        title: 'Tid',
        summary: 'Följ utveckling över tid med linjediagram.'
      },
      [WidgetType.LIST]: {
        disabledTitle: 'Detta mätetal går inte att visa som lista eller tabell',
        title: 'Tabell',
        summary: 'Visa detaljerad data i tabellformat.'
      },
      [WidgetType.MAP]: {
        disabledTitle: 'Detta mätetal går inte att visa på karta',
        title: 'Karta',
        summary: 'Visa data geografiskt på en karta.'
      },
      [WidgetType.COMBO_CHART]: {
        disabledTitle:
          'Detta mätetal går inte att visa i ett kombineringsdiagram',
        title: 'Kombo',
        summary: 'Kombinera flera diagramtyper i en visualisering.'
      },
      [WidgetType.PIE_CHART]: {
        disabledTitle: 'Detta mätetal går inte att visa som tårtdiagram',
        title: 'Cirkel',
        summary: 'Visa fördelning i ett cirkeldiagram.'
      },
      [WidgetType.FUNNEL]: {
        disabledTitle: 'Detta mätetal går inte att visa som flöde',
        title: 'Flöde',
        summary: 'Visa stegvisa processer i ett flödesschema.'
      },
      [WidgetType.TEXT]: {
        disabledTitle: 'Går inte att visa som text',
        title: 'Rubrik',
        summary: 'Skapa en rubrik för sammanhängande widgets.'
      },
      [WidgetType.COMMENT]: {
        disabledTitle: 'Går inte att visa som kommentar',
        title: 'Kommentar',
        summary: 'Skapa ett tomt fält för kommentarer efter export.'
      },
      [WidgetType.IMAGE]: {
        disabledTitle: 'Går inte att visa som bild',
        title: 'Bild',
        summary: 'Skapa och visa en bild via extern länk.'
      },
      [WidgetType.RICH_TEXT]: {
        disabledTitle: 'Går inte att visa som text',
        title: 'Text',
        summary: 'Skapa ett textstycke med formateringsmöjligheter.'
      }
    },
    rolling_types: {
      [RollingType.R7]: {
        abbreviation: 'R7',
        title: 'Rullande 7 dagar',
        helperText: 'Summan av de senaste 7 dagarnas värden.'
      },
      [RollingType.R30]: {
        abbreviation: 'R30',
        title: 'Rullande 30 dagar',
        helperText: 'Summan av de senaste 30 dagarnas värden.'
      },
      [RollingType.R90]: {
        abbreviation: 'R90',
        title: 'Rullande 90 dagar',
        helperText: 'Summan av de senaste 90 dagarnas värden.'
      },
      [RollingType.R365]: {
        abbreviation: 'R365',
        title: 'Rullande 365 dagar',
        helperText: 'Summan av de senaste 365 dagarnas värden.'
      },
      [RollingType.R12]: {
        abbreviation: 'R12',
        title: 'Rullande 12 månader',
        helperText: 'Summan av de senaste 12 månadernas värden.'
      }
    },
    breakdown_by_display_types: {
      [BreakdownByDisplayType.CLOSE_BY]: {
        title: 'Närliggande per segmentering'
      },
      [BreakdownByDisplayType.STACKED]: {
        title: 'Staplade per segmentering'
      },
      [BreakdownByDisplayType.STACKED_100_PERCENT]: {
        title: '100% staplade per segmentering'
      }
    },
    api_key_types: {
      [ApiKeyType.DASHBOARD]: {
        title: 'Tillgång till dashboards',
        body: 'Används för att bädda in dashboards på externa webbplatser.'
      },
      [ApiKeyType.EXPORT_TABLE]: {
        title: 'Tillgång till tabeller',
        body: 'Används för att hämta tabelldata via API-anrop.'
      }
    },
    metric_statuses: {
      [MetricStatus.ACTIVE]: {
        pluralTitle: 'Aktiverade',
        title: 'Aktiverad'
      },
      [MetricStatus.INACTIVE]: {
        pluralTitle: 'Inaktiverade',
        title: 'Inaktiverad'
      },
      [MetricStatus.MISSING_DATA]: {
        pluralTitle: 'Data saknas',
        title: 'Data saknas'
      },
      [MetricStatus.MISSING_SLICES]: {
        pluralTitle: 'Skärningar saknas',
        title: 'Skärningar saknas'
      },
      [MetricStatus.MISSING_DEFINITION]: {
        pluralTitle: 'Definition saknas',
        title: 'Definition saknas'
      }
    },
    account_roles: {
      [EAccountRole.ADMIN]: {
        title: 'Systemadministratör',
        summary:
          'Full åtkomst. Hantera alla resurser, grupper and inställningar.'
      },
      [EAccountRole.VIEW]: {
        title: 'Användare',
        summary: 'Visa delade resurser. Skapa och redigera egna resurser.'
      },
      [EAccountRole.ACCESS_HUB]: {
        title: 'Access hub',
        summary: 'Endast API-inställningar.'
      }
    },
    dashboard_activity_periods: {
      ['THIS_WEEK']: { title: 'Den här veckan' },
      ['THIS_MONTH']: { title: 'Den här månaden' },
      ['THIS_YEAR']: { title: 'Det här året' },
      ['SINCE_START']: { title: 'Sedan start' }
    },
    snackbar_messages: {
      // Accounting
      account_level_created: 'Kontonivå skapad',
      account_level_created_failed: 'Kunde inte skapa kontonivå',
      account_level_updated: 'Kontonivå uppdaterad',
      account_level_updated_failed: 'Kunde inte uppdatera kontonivå',
      account_level_deleted: 'Kontonivå borttagen',
      account_level_deleted_failed: 'Kunde inte ta bort kontonivå',

      // API keys
      api_key_created: 'API-nyckel skapad',
      api_key_created_failed: 'Kunde inte skapa API-nyckel',
      api_key_deleted: 'API-nyckel borttagen',
      api_key_deleted_failed: 'Kunde inte ta bort API-nyckel',
      export_tables_assigned_to_api_key: 'Åtkomst uppdaterad',
      export_tables_assigned_to_api_key_failed: 'Kunde inte uppdatera åtkomst',

      // MCP tokens

      // Chart of accounts
      chart_of_accounts_created: 'Kontoplan skapad',
      chart_of_accounts_created_failed: 'Kunde inte skapa kontoplan',
      chart_of_accounts_updated: 'Kontoplan uppdaterad',
      chart_of_accounts_updated_failed: 'Kunde inte uppdatera kontoplan',
      chart_of_accounts_deleted: 'Kontoplan borttagen',
      chart_of_accounts_deleted_failed: 'Kunde inte ta bort kontoplan',

      // Company groups (customers)
      company_group_created: 'Kund skapad',
      company_group_created_failed: 'Kunde inte skapa kund',
      company_group_deleted: 'Kund borttagen',
      company_group_deleted_failed: 'Kunde inte ta bort kund',
      company_group_updated: 'Kund uppdaterad',
      company_group_updated_failed: 'Kunde inte uppdatera kund',
      ontology_rerun_started: 'Datauppdatering startad',
      ontology_rerun_started_failed: 'Kunde inte starta datauppdateringen',

      // Dashboards
      dashboard_created: 'Dashboard skapad',
      dashboard_created_failed: 'Kunde inte skapa dashboard',
      dashboard_duplicated: 'Dashboard duplicerad',
      dashboard_duplicated_failed: 'Kunde inte duplicera dashboard',
      dashboard_updated: 'Dashboard uppdaterad',
      dashboard_updated_failed: 'Kunde inte uppdatera dashboard',
      dashboard_deleted: 'Dashboard borttagen',
      dashboard_deleted_failed: 'Kunde inte ta bort dashboard',
      dashboard_restored: 'Dashboard återställd',
      dashboard_restored_failed: 'Kunde inte återställa dashboard',

      // Analyses
      analysis_type_updated: 'Analyspaket uppdaterat',
      analysis_type_updated_failed: 'Kunde inte uppdatera analyspaket',

      // Analyses
      analysis_deleted: 'Analys borttagen',
      analysis_deleted_failed: 'Kunde inte ta bort analys',
      analysis_rerun: 'Analysen är körd igen.',
      analysis_rerun_failed:
        'Analysen kunde inte köras om. Försök igen om en stund.',
      analysis_rerun_cancelled:
        'Körningen är avbruten. Analysen är oförändrad.',

      // Groups
      access_requested: 'Åtkomstförfrågan skickad',
      access_requested_failed: 'Kunde inte skicka åtkomstförfrågan',
      group_created: 'Grupp skapad',
      group_created_failed: 'Kunde inte skapa grupp',
      group_deleted: 'Grupp borttagen',
      group_deleted_failed: 'Kunde inte ta bort grupp',
      group_updated: 'Grupp uppdaterad',
      group_updated_failed: 'Kunde inte uppdatera grupp',
      you_joined_group: 'Du gick med i gruppen',
      you_joined_group_failed: 'Kunde inte gå med i gruppen',
      you_left_group: 'Du lämnade gruppen',
      you_left_group_failed: 'Kunde inte lämna gruppen',

      // Entities
      columns_updated: 'Kolumner uppdaterade',
      columns_updated_failed: 'Kunde inte uppdatera kolumner',
      slice_created: 'Skärning skapad',
      slice_created_failed: 'Kunde inte skapa skärning',
      slice_deleted: 'Skärning borttagen',
      slice_deleted_failed: 'Kunde inte ta bort skärning',
      slice_updated: 'Skärning uppdaterad',
      slice_updated_failed: 'Kunde inte uppdatera skärning',

      // Export Tables
      export_table_created: 'Tabell skapad',
      export_table_created_failed: 'Kunde inte skapa tabell',
      export_table_updated: 'Tabell uppdaterad',
      export_table_updated_failed: 'Kunde inte uppdatera tabell',
      export_table_deleted: 'Tabell borttagen',
      export_table_deleted_failed: 'Kunde inte ta bort tabell',
      export_table_api_keys_assigned_success: 'Åtkomst uppdaterad',
      export_table_api_keys_assigned_failed: 'Kunde inte uppdatera åtkomst',
      export_table_column_created: 'Kolumn tillagd',
      export_table_column_created_failed: 'Kunde inte lägga till kolumn',
      export_table_column_updated: 'Kolumn uppdaterad',
      export_table_column_updated_failed: 'Kunde inte uppdatera kolumn',
      export_table_column_deleted: 'Kolumn borttagen',
      export_table_column_deleted_failed: 'Kunde inte ta bort kolumn',

      // Filters
      filter_created: 'Filter skapat',
      filter_created_failed: 'Kunde inte skapa filter',
      filter_updated: 'Filter uppdaterat',
      filter_updated_failed: 'Kunde inte uppdatera filter',
      filter_deleted: 'Filter borttaget',
      filter_deleted_failed: 'Kunde inte ta bort filter',

      // Folders
      folder_created: 'Mapp skapad',
      folder_created_failed: 'Kunde inte skapa mapp',
      folder_updated: 'Mapp uppdaterad',
      folder_updated_failed: 'Kunde inte uppdatera mapp',
      folder_deleted: 'Mapp borttagen',
      folder_deleted_failed: 'Kunde inte ta bort mapp',

      // Tags
      tag_created: 'Tagg skapad',
      tag_created_failed: 'Kunde inte skapa tagg',

      // KPI:s
      kpi_created: 'Nyckeltal skapat',
      kpi_created_failed: 'Kunde inte skapa nyckeltal',
      kpi_updated: 'Nyckeltal uppdaterat',
      kpi_updated_failed: 'Kunde inte uppdatera nyckeltal',
      kpi_deleted: 'Nyckeltal borttaget',
      kpi_deleted_failed: 'Kunde inte ta bort nyckeltal',
      kpi_order_updated: 'Nyckeltalsordning uppdaterad',
      kpi_order_updated_failed: 'Kunde inte uppdatera nyckeltalsordning',

      // Metrics
      metric_created: 'Mätetal skapat',
      metric_created_failed: 'Kunde inte skapa mätetal',
      metric_updated: 'Mätetal uppdaterat',
      metric_updated_failed: 'Kunde inte uppdatera mätetal',
      metric_deleted: 'Mätetal borttaget',
      metric_deleted_failed: 'Kunde inte ta bort mätetal',
      metric_definition_removed: 'Definition borttagen',
      metric_definition_removed_failed: 'Kunde inte ta bort definition',

      // Reports
      report_duplicated: 'Rapport duplicerad',
      report_duplicated_failed: 'Kunde inte duplicera rapport',
      report_published: 'Rapport publicerad',
      report_published_failed: 'Kunde inte publicera rapport',
      report_unpublished: 'Rapport avpublicerad',
      report_unpublished_failed: 'Kunde inte avpublicera rapport',
      report_edition_published: 'Utgåva publicerad',
      report_edition_published_failed: 'Kunde inte publicera utgåva',
      report_edition_unpublished: 'Utgåva avpublicerad',
      report_edition_unpublished_failed: 'Kunde inte avpublicera utgåva',

      // Tabs (observera stavningen i enum)
      tab_created: 'Flik skapad',
      tab_created_failed: 'Kunde inte skapa flik',
      tab_duplicated: 'Flik duplicerad',
      tab_duplicated_failed: 'Kunde inte duplicera flik',
      tab_deleleted: 'Flik borttagen',
      tab_deleleted_failed: 'Kunde inte ta bort flik',
      tab_moved: 'Flik flyttad',
      tab_moved_failed: 'Kunde inte flytta flik',

      // Tasks
      task_created: 'Aktivitet skapad',
      task_created_failed: 'Kunde inte skapa aktivitet',
      task_updated: 'Aktivitet uppdaterad',
      task_updated_failed: 'Kunde inte uppdatera aktivitet',
      task_deleted: 'Aktivitet borttagen',
      task_deleted_failed: 'Kunde inte ta bort aktivitet',

      // Users
      user_created: 'Användare skapad',
      user_created_failed: 'Kunde inte skapa användare',
      user_updated: 'Användare uppdaterad',
      user_updated_failed: 'Kunde inte uppdatera användare',
      user_deleted: 'Användare borttagen',
      user_deleted_failed: 'Kunde inte ta bort användare',

      // Widgets
      widget_created: 'Widget skapad',
      widget_created_failed: 'Kunde inte skapa widget',
      widget_updated: 'Widget uppdaterad',
      widget_updated_failed: 'Kunde inte uppdatera widget',
      widget_deleted: 'Widget borttagen',
      widget_deleted_failed: 'Kunde inte ta bort widget',

      // Settings
      settings_saved: 'Inställningar sparade',
      settings_saved_failed: 'Kunde inte spara inställningar',

      // Organizaitional units
      organizational_unit_created: 'Dataåtkomstgrupp skapad',
      organizational_unit_created_failed: 'Kunde inte skapa dataåtkomstgrupp',
      organizational_unit_updated: 'Dataåtkomstgrupp uppdaterad',
      organizational_unit_updated_failed:
        'Kunde inte uppdatera dataåtkomstgrupp',
      organizational_unit_deleted: 'Dataåtkomstgrupp borttagen',
      organizational_unit_deleted_failed: 'Kunde inte ta bort dataåtkomstgrupp',

      // AI chat
      chat_feedback_sent: 'Tack för din feedback!',
      chat_feedback_sent_failed: 'Kunde inte skicka din feedback',

      // General
      copied_to_clipboard: 'Kopierat till urklipp',
      copied_to_clipboard_failed: 'Kunde inte kopiera till urklipp',
      file_uploaded: 'Fil uppladdad',
      file_uploaded_failed: 'Kunde inte ladda upp fil',
      file_exported: 'Fil exporterad',
      file_exported_failed: 'Kunde inte exportera fil',
      file_download_failed: 'Kunde inte ladda ner fil',
      request_sent: 'Förfrågan skickad',
      request_sent_failed: 'Kunde inte skicka förfrågan',

      // Generic internal UI only
      success: 'Klart',
      failed: 'Misslyckades'
    },
    snackbar_link_texts: {
      [SnackbarLinkText.NavigateToDashboard]: 'Till dashboard'
    },
    comparative_periods: {
      [DateFilterType.PERIOD]: {
        // PERIOD FILTERS
        [PeriodFilter.THIS_WEEK_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Föregående vecka',
          [ComparativePeriod.LAST_YEAR]: 'Samma period föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: 'Genomsnitt för 7 dagar'
        },
        [PeriodFilter.THIS_MONTH_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Föregående månad',
          [ComparativePeriod.LAST_YEAR]: 'Samma period föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: 'Genomsnitt för en månad'
        },
        [PeriodFilter.THIS_QUARTER_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Föregående kvartal',
          [ComparativePeriod.LAST_YEAR]: 'Samma period föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.THIS_YEAR_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Föregående år',
          [ComparativePeriod.LAST_YEAR]: 'Samma period föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Igår',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: 'Genomsnitt för en dag'
        },
        [PeriodFilter.YESTERDAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'I förrgår',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: 'Genomsnitt för en dag'
        },
        [PeriodFilter.LAST_WEEK]: {
          [ComparativePeriod.LAST_PERIOD]: 'Föregående vecka',
          [ComparativePeriod.LAST_YEAR]: 'Samma period föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: 'Genomsnitt för 7 dagar'
        },
        [PeriodFilter.LAST_MONTH]: {
          [ComparativePeriod.LAST_PERIOD]: 'Föregående månad',
          [ComparativePeriod.LAST_YEAR]: 'Samma period föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: 'Genomsnitt för en månad'
        },
        [PeriodFilter.LAST_QUARTER]: {
          [ComparativePeriod.LAST_PERIOD]: 'Föregående kvartal',
          [ComparativePeriod.LAST_YEAR]: 'Samma period föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_YEAR]: {
          [ComparativePeriod.LAST_PERIOD]: 'Föregående år',
          [ComparativePeriod.LAST_YEAR]: 'Samma period föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_7_DAYS]: {
          [ComparativePeriod.LAST_PERIOD]: 'Föregående 7 dagar',
          [ComparativePeriod.LAST_YEAR]: 'Samma period föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: 'Genomsnitt för 7 dagar'
        },
        [PeriodFilter.LAST_30_DAYS]: {
          [ComparativePeriod.LAST_PERIOD]: 'Föregående 30 dagar',
          [ComparativePeriod.LAST_YEAR]: 'Samma period föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: 'Genomsnitt för 30 dagar'
        },
        [PeriodFilter.LAST_90_DAYS]: {
          [ComparativePeriod.LAST_PERIOD]: 'Föregående 90 dagar',
          [ComparativePeriod.LAST_YEAR]: 'Samma period föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_365_DAYS]: {
          [ComparativePeriod.LAST_PERIOD]: 'Föregående 365 dagar',
          [ComparativePeriod.LAST_YEAR]: 'Samma period föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LATEST_MONTH_WITH_DATA]: {
          [ComparativePeriod.LAST_PERIOD]: 'Föregående månad',
          [ComparativePeriod.LAST_YEAR]: 'Samma period föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_3_MONTHS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Föregående 3 månader',
          [ComparativePeriod.LAST_YEAR]: 'Samma period föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_6_MONTHS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Föregående 6 månader',
          [ComparativePeriod.LAST_YEAR]: 'Samma period föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_12_MONTHS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Föregående 12 månader',
          [ComparativePeriod.LAST_YEAR]: 'Samma period föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_4_QUARTERS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Föregående 4 kvartalen',
          [ComparativePeriod.LAST_YEAR]: 'Samma period föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_24_MONTHS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Föregående 24 månader',
          [ComparativePeriod.LAST_YEAR]: 'Samma period föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_36_MONTHS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Föregående 36 månader',
          [ComparativePeriod.LAST_YEAR]: 'Samma period föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_2_YEARS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Föregående 2 år',
          [ComparativePeriod.LAST_YEAR]: 'Samma period föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_3_YEARS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Föregående 3 år',
          [ComparativePeriod.LAST_YEAR]: 'Samma period föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_8_QUARTERS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Föregående 8 kvartal',
          [ComparativePeriod.LAST_YEAR]: 'Samma period föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_12_QUARTERS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Föregående 12 kvartal',
          [ComparativePeriod.LAST_YEAR]: 'Samma period föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.CURRENT_FINANCIAL_YEAR]: {
          [ComparativePeriod.LAST_PERIOD]: 'Samma period föregående år',
          [ComparativePeriod.LAST_YEAR]: 'Samma period föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.PREVIOUS_FINANCIAL_YEAR]: {
          [ComparativePeriod.LAST_PERIOD]: 'Samma period föregående år',
          [ComparativePeriod.LAST_YEAR]: 'Samma period föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_3_FINANCIAL_YEARS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: '-',
          [ComparativePeriod.LAST_YEAR]: '-',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        }
      },
      [DateFilterType.POINT]: {
        // POINTS
        [PeriodFilter.THIS_WEEK_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Samma dag föregående vecka',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.THIS_MONTH_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Samma dag föregående månad',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.THIS_QUARTER_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Samma dag föregående kvartal',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.THIS_YEAR_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Samma dag föregående år',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Igår',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.YESTERDAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'I förrgår',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_WEEK]: {
          [ComparativePeriod.LAST_PERIOD]: 'Samma dag föregående vecka',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_MONTH]: {
          [ComparativePeriod.LAST_PERIOD]: 'Samma dag föregående månad',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_QUARTER]: {
          [ComparativePeriod.LAST_PERIOD]: 'Samma dag föregående kvartal',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_YEAR]: {
          [ComparativePeriod.LAST_PERIOD]: '-',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_7_DAYS]: {
          [ComparativePeriod.LAST_PERIOD]: '7 dagar tidigare',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_30_DAYS]: {
          [ComparativePeriod.LAST_PERIOD]: '30 dagar tidigare',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_90_DAYS]: {
          [ComparativePeriod.LAST_PERIOD]: '90 dagar tidigare',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_365_DAYS]: {
          [ComparativePeriod.LAST_PERIOD]: '365 dagar tidigare',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LATEST_MONTH_WITH_DATA]: {
          [ComparativePeriod.LAST_PERIOD]: 'Samma dag föregående månad',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_3_MONTHS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Samma dag 3 månader tidigare',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_6_MONTHS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Samma dag 6 månader tidigare',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_12_MONTHS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Samma dag 12 månader tidigare',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_4_QUARTERS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Samma dag 4 kvartal tidigare',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_24_MONTHS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Samma dag 24 månader tidigare',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_36_MONTHS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Samma dag 36 månader tidigare',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_2_YEARS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Samma dag 2 år tidigare',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_3_YEARS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Samma dag 3 år tidigare',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_8_QUARTERS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Samma dag 8 kvartal tidigare',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_12_QUARTERS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Samma dag 12 kvartal tidigare',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.CURRENT_FINANCIAL_YEAR]: {
          [ComparativePeriod.LAST_PERIOD]: 'Samma dag föregående år',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.PREVIOUS_FINANCIAL_YEAR]: {
          [ComparativePeriod.LAST_PERIOD]: 'Samma dag föregående år',
          [ComparativePeriod.LAST_YEAR]: 'Samma dag föregående år',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_3_FINANCIAL_YEARS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: '-',
          [ComparativePeriod.LAST_YEAR]: '-',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        }
      }
    }
  },
  en: {
    priority: {
      [EPriority.HIGH]: {
        iconName: 'High',
        title: 'High'
      },
      [EPriority.MEDIUM]: {
        iconName: 'Medium',
        title: 'Medium'
      },
      [EPriority.LOW]: {
        iconName: 'Low',
        title: 'Low'
      }
    },
    report_status: {
      [EPublicationStatus.DRAFT]: 'Draft',
      [EPublicationStatus.PUBLISHED]: 'Published'
    },
    analysis_status: {
      [EAnalysisStatus.UNHANDLED]: 'Unhandled',
      [EAnalysisStatus.ACCEPTED]: 'Accepted',
      [EAnalysisStatus.REJECTED]: 'Rejected'
    },
    source_types: {
      DASHBOARD: 'Dashboard',
      GLOBAL: 'Global',
      KEY_METRIC: 'Key metric',
      REPORT_EDITION: 'Report edition',
      ANALYSIS: 'Analysis'
    },
    forecast_periods: {
      [ForecastPeriod.FILL_CURRENT_WEEK]: { title: 'Rest of the week' },
      [ForecastPeriod.FILL_CURRENT_MONTH]: { title: 'Rest of the month' },
      [ForecastPeriod.FILL_CURRENT_QUARTER]: { title: 'Rest of the quarter' },
      [ForecastPeriod.FILL_CURRENT_YEAR]: { title: 'Rest of the year' },
      [ForecastPeriod.FILL_CURRENT_YEAR_AND_NEXT_YEAR]: {
        title: 'Rest of the year and next year'
      },
      [ForecastPeriod.FILL_1_MONTH_AHEAD]: {
        title: 'Up to next month'
      },
      [ForecastPeriod.FILL_3_MONTH_AHEAD]: {
        title: 'Up to 3 months ahead'
      },
      [ForecastPeriod.FILL_6_MONTH_AHEAD]: {
        title: 'Up to 6 months ahead'
      },
      [ForecastPeriod.FILL_12_MONTH_AHEAD]: {
        title: 'Up to 12 months ahead'
      },
      [ForecastPeriod.FILL_3_YEARS_AHEAD]: {
        title: 'Up to 3 years ahead'
      },
      [ForecastPeriod.FILL_5_YEARS_AHEAD]: {
        title: 'Up to 5 years ahead'
      },
      [ForecastPeriod.FILL_10_YEARS_AHEAD]: {
        title: 'Up to 10 years ahead'
      },
      [ForecastPeriod.FILL_25_YEARS_AHEAD]: {
        title: 'Up to 25 years ahead'
      },
      [ForecastPeriod.FILL_50_YEARS_AHEAD]: {
        title: 'Up to 50 years ahead'
      }
    },
    flaw_types: {
      [FlawType.DUPLICATE]: { title: 'Duplicate' },
      [FlawType.IS_NULL]: { title: 'Missing value' }
    },
    analysis_type_status: {
      ACTIVE: {
        color: 'success',
        pluralTitle: 'Activated',
        title: 'Activated'
      },
      INACTIVE: {
        color: undefined,
        pluralTitle: 'Deactivated',
        title: 'Deactivated'
      },
      MISSING_CONFIGURATION: {
        color: undefined,
        pluralTitle: 'Missing configuration',
        title: 'Missing configuration'
      }
    },
    task_status: {
      DONE: { color: 'success', iconName: 'Done', title: 'Done' },
      IN_PROGRESS: {
        color: 'info',
        iconName: 'InProgress',
        title: 'In progress'
      },
      TODO: {
        color: undefined,
        iconName: 'Todo',
        title: 'Todo'
      }
    },
    general_config_attributes: {
      [GeneralConfigAttribute.NumberOfDaysForNewlyRentedObjects]: {
        title:
          'Number of days an object is considered newly rented since contract start'
      },
      [GeneralConfigAttribute.ObjectRowTypes]: {
        title: 'Charge types on objects'
      }
    },
    period_types: {
      [PeriodType.QUARTERLY]: 'Quarterly',
      [PeriodType.MONTHLY]: 'Monthly',
      [PeriodType.MANUALLY]: 'Manually'
    },
    show_pers: {
      [ShowPerEnum.RENTABLE_SQM]: {
        abbreviation: 'RNTB',
        helperText: 'Distributed on every rentable sqm.',
        title: 'Rentable sqm'
      },
      [ShowPerEnum.ATEMP]: {
        abbreviation: 'ATEMP',
        helperText: 'Distributed on every heated sqm (10 °C).',
        title: 'Heated sqm (10 °C)'
      },
      [ShowPerEnum.BTA]: {
        abbreviation: 'GROSS',
        helperText: 'Distributed on every gross area sqm.',
        title: 'Gross area sqm'
      },
      [ShowPerEnum.NUMBER_OF_OBJECTS]: {
        abbreviation: 'UNITS',
        helperText: 'Distributed on every rental units.',
        title: 'Rental units'
      },
      [ShowPerEnum.NUMBER_OF_DWELLINGS]: {
        abbreviation: 'DWL',
        helperText: 'Distributed on every dwelling.',
        title: 'Dwellings'
      },
      [ShowPerEnum.NUMBER_OF_SPACES]: {
        abbreviation: 'ROOM',
        helperText: 'Distributed on every room.',
        title: 'Rooms'
      },
      [ShowPerEnum.NUMBER_OF_UNITS]: {
        abbreviation: 'UNT',
        helperText: 'Distributed on every unit.',
        title: 'Units'
      },
      [ShowPerEnum.RENTED_SQM]: {
        abbreviation: 'RNTD',
        helperText: 'Distributed on every rented sqm.',
        title: 'Rented sqm'
      },
      [ShowPerEnum.NUMBER_OF_DAYS]: {
        abbreviation: 'DAY',
        helperText: 'Distributed on every day.',
        title: 'Days'
      },
      [ShowPerEnum.NUMBER_OF_WORKDAYS]: {
        abbreviation: 'W.DAY',
        helperText: 'Distributed on every working day.',
        title: 'Workdays'
      },
      [ShowPerEnum.NUMBER_OF_MOVE_INS]: {
        abbreviation: 'MOV.I',
        helperText: 'Distributed on every move-in.',
        title: 'Move-ins'
      },
      [ShowPerEnum.NUMBER_OF_SNOW_DAYS]: {
        abbreviation: 'SNOW',
        helperText:
          'Distributed on every day with snow at the nearest SMHI station.',
        title: 'Snow days'
      }
    },
    group_by_types: {
      SEGMENT_BY: { title: 'Show by' },
      BREAKDOWN_BY: { title: 'Break down by' }
    },
    period_filters: {
      [PeriodFilter.TODAY]: { title: 'Today', short: 'Today' },
      [PeriodFilter.YESTERDAY]: { title: 'Yesterday', short: 'Yest.' },
      [PeriodFilter.THIS_WEEK_TO_TODAY]: {
        title: 'Week to date',
        short: 'WTD'
      },
      [PeriodFilter.THIS_MONTH_TO_TODAY]: {
        title: 'Month to date',
        short: 'MTD'
      },
      [PeriodFilter.THIS_QUARTER_TO_TODAY]: {
        title: 'Quarter to date',
        short: 'QTD'
      },
      [PeriodFilter.THIS_YEAR_TO_TODAY]: {
        title: 'Year to date',
        short: 'YTD'
      },
      [PeriodFilter.LAST_WEEK]: { title: 'Last week', short: 'LW' },
      [PeriodFilter.LAST_MONTH]: { title: 'Last month', short: 'LM' },
      [PeriodFilter.LAST_QUARTER]: { title: 'Last quarter', short: 'LQ' },
      [PeriodFilter.LAST_YEAR]: { title: 'Last year', short: 'LY' },
      [PeriodFilter.LAST_7_DAYS]: { title: 'Past 7 days', short: '7d' },
      [PeriodFilter.LAST_30_DAYS]: { title: 'Past 30 days', short: '30d' },
      [PeriodFilter.LAST_90_DAYS]: { title: 'Past 90 days', short: '90d' },
      [PeriodFilter.LAST_365_DAYS]: { title: 'Past 365 days', short: '365d' },
      [PeriodFilter.LATEST_MONTH_WITH_DATA]: {
        title: 'Latest month with data',
        short: 'Latest mo.'
      },
      [PeriodFilter.LAST_3_MONTHS_TO_TODAY]: {
        title: 'Past 3 months',
        short: '3mo'
      },
      [PeriodFilter.LAST_6_MONTHS_TO_TODAY]: {
        title: 'Past 6 months',
        short: '6mo'
      },
      [PeriodFilter.LAST_12_MONTHS_TO_TODAY]: {
        title: 'Past 12 months',
        short: '12mo'
      },
      [PeriodFilter.LAST_24_MONTHS_TO_TODAY]: {
        title: 'Past 24 months',
        short: '24mo'
      },
      [PeriodFilter.LAST_36_MONTHS_TO_TODAY]: {
        title: 'Past 36 months',
        short: '36mo'
      },
      [PeriodFilter.LAST_2_YEARS_TO_TODAY]: {
        title: 'Past 2 years',
        short: '2y'
      },
      [PeriodFilter.LAST_3_YEARS_TO_TODAY]: {
        title: 'Past 3 years',
        short: '3y'
      },
      [PeriodFilter.LAST_4_QUARTERS_TO_TODAY]: {
        title: 'Past 4 quarters',
        short: '4Q'
      },
      [PeriodFilter.LAST_8_QUARTERS_TO_TODAY]: {
        title: 'Past 8 quarters',
        short: '8Q'
      },
      [PeriodFilter.LAST_12_QUARTERS_TO_TODAY]: {
        title: 'Past 12 quarters',
        short: '12Q'
      },
      [PeriodFilter.CURRENT_FINANCIAL_YEAR]: {
        title: 'Current financial year',
        short: 'CFY'
      },
      [PeriodFilter.PREVIOUS_FINANCIAL_YEAR]: {
        title: 'Previous financial year',
        short: 'PFY'
      },
      [PeriodFilter.LAST_3_FINANCIAL_YEARS_TO_TODAY]: {
        title: 'Past 3 financial years',
        short: '3FY'
      }
    },
    conditions: {
      [Condition.IS_NULL]: 'is empty',
      [Condition.EQ]: 'is equal to',
      [Condition.NE]: 'is not equal to',
      [Condition.LTE]: 'is less than or equal to',
      [Condition.LT]: 'is less than',
      [Condition.GTE]: 'is greater than or equal to',
      [Condition.GT]: 'is greater than',
      [Condition.IS_NOT_NULL]: 'is not empty'
    },
    key_figure_visualization_types: {
      [KeyFigureVisualizationType.FOCUS]: 'Focus',
      [KeyFigureVisualizationType.LIST]: 'List'
    },
    pie_chart_value_visualization_types: {
      [PieChartValueVisualizationType.VALUES]: 'Values',
      [PieChartValueVisualizationType.PERCENTAGE]: 'Percentage',
      [PieChartValueVisualizationType.VALUES_AND_PERCENTAGE]:
        'Values and percentage'
    },
    text_aligns: {
      [TextAlign.LEFT]: 'Left',
      [TextAlign.CENTER]: 'Center',
      [TextAlign.RIGHT]: 'Right'
    },
    text_vertical_aligns: {
      [TextVerticalAlign.TOP]: 'Top',
      [TextVerticalAlign.CENTER]: 'Middle',
      [TextVerticalAlign.BOTTOM]: 'Bottom'
    },
    categories: {
      [Category.ENERGY]: 'Sustainability',
      [Category.RENTAL]: 'Leasing',
      [Category.REAL_ESTATE_PORTFOLIO]: 'Portfolio',
      [Category.TECHNICAL_MANAGEMENT]: 'Operations',
      [Category.ECONOMY]: 'Finance',
      [Category.HOMEPAL]: 'Homepal'
    },
    comparative_display_types: {
      [ComparativeDisplayType.PERCENTAGE]: {
        title: 'Change',
        tooltip: 'Show only change'
      },
      [ComparativeDisplayType.VALUE]: {
        title: 'Values',
        tooltip: 'Show only values'
      },
      [ComparativeDisplayType.PERCENTAGE_AND_VALUE]: {
        title: 'Both',
        tooltip: 'Show both value and change'
      },
      [ComparativeDisplayType.PREVIOUS_VALUE]: {
        title: 'Previous',
        tooltip: 'Show only the value of the previous period'
      },
      [ComparativeDisplayType.HIDDEN]: {
        title: 'None',
        tooltip: 'Hide change'
      }
    },
    widget_types: {
      [WidgetType.KEY_FIGURE]: {
        disabledTitle: 'This metric cannot be displayed as a number',
        title: 'Number',
        summary: 'Display a key figure as a single number.'
      },
      [WidgetType.BAR_CHART]: {
        disabledTitle: 'This metric cannot be displayed as bars',
        title: 'Bars',
        summary: 'Visualize data as a bar chart.'
      },
      [WidgetType.BUBBLE_CHART]: {
        disabledTitle: 'This metric cannot be displayed as a bubble chart',
        title: 'Bubbles',
        summary: 'Compare data points in a bubble chart.'
      },
      [WidgetType.LINE_CHART]: {
        disabledTitle: 'This metric cannot be displayed over time',
        title: 'Time',
        summary: 'Track trends over time with a line chart.'
      },
      [WidgetType.LIST]: {
        disabledTitle: 'This metric cannot be displayed as a list or table',
        title: 'Table',
        summary: 'Display detailed data in table format.'
      },
      [WidgetType.MAP]: {
        disabledTitle: 'This metric cannot be displayed on a map',
        title: 'Map',
        summary: 'Display data geographically on a map.'
      },
      [WidgetType.COMBO_CHART]: {
        disabledTitle: 'This metric cannot be displayed in a combo chart',
        title: 'Combo',
        summary: 'Combine multiple chart types in one visualization.'
      },
      [WidgetType.PIE_CHART]: {
        disabledTitle: 'This metric cannot be displayed as a pie chart',
        title: 'Pie',
        summary: 'Show distribution in a pie chart.'
      },
      [WidgetType.FUNNEL]: {
        disabledTitle: 'This metric cannot be displayed as a funnel',
        title: 'Funnel',
        summary: 'Visualize step-by-step processes in a funnel chart.'
      },
      [WidgetType.TEXT]: {
        disabledTitle: 'Cannot be displayed as text',
        title: 'Heading',
        summary: 'Create a heading for grouped widgets.'
      },
      [WidgetType.COMMENT]: {
        disabledTitle: 'Cannot be displayed as a comment',
        title: 'Comment',
        summary: 'Add an empty field for comments after export.'
      },
      [WidgetType.IMAGE]: {
        disabledTitle: 'Cannot be displayed as an image',
        title: 'Image',
        summary: 'Insert and display an image via external link.'
      },
      [WidgetType.RICH_TEXT]: {
        disabledTitle: 'Cannot be displayed as text',
        title: 'Text',
        summary: 'Add a text section with formating options.'
      }
    },
    rolling_types: {
      [RollingType.R7]: {
        abbreviation: 'R7',
        title: 'Rolling 7 days',
        helperText: 'Sum of the last 7 days\' values.'
      },
      [RollingType.R30]: {
        abbreviation: 'R30',
        title: 'Rolling 30 days',
        helperText: 'Sum of the last 30 days\' values.'
      },
      [RollingType.R90]: {
        abbreviation: 'R90',
        title: 'Rolling 90 days',
        helperText: 'Sum of the last 90 days\' values.'
      },
      [RollingType.R365]: {
        abbreviation: 'R365',
        title: 'Rolling 365 days',
        helperText: 'Sum of the last 365 days\' values.'
      },
      [RollingType.R12]: {
        abbreviation: 'R12',
        title: 'Rolling 12 months',
        helperText: 'Sum of the last 12 months\' values.'
      }
    },
    breakdown_by_display_types: {
      [BreakdownByDisplayType.CLOSE_BY]: {
        title: 'Närliggande per segmentering'
      },
      [BreakdownByDisplayType.STACKED]: {
        title: 'Staplade per segmentering'
      },
      [BreakdownByDisplayType.STACKED_100_PERCENT]: {
        title: '100% staplade per segmentering'
      }
    },
    api_key_types: {
      [ApiKeyType.DASHBOARD]: {
        title: 'Access to dashboards',
        body: 'Used for embedding dashboards on external websites.'
      },
      [ApiKeyType.EXPORT_TABLE]: {
        title: 'Access to tables',
        body: 'Used for retrieving table data via API calls.'
      }
    },
    metric_statuses: {
      [MetricStatus.ACTIVE]: {
        pluralTitle: 'Active',
        title: 'Active'
      },
      [MetricStatus.INACTIVE]: {
        pluralTitle: 'Disabled',
        title: 'Disabled'
      },
      [MetricStatus.MISSING_DATA]: {
        pluralTitle: 'Missing data',
        title: 'Missing data'
      },
      [MetricStatus.MISSING_SLICES]: {
        pluralTitle: 'Missing slices',
        title: 'Missing slices'
      },
      [MetricStatus.MISSING_DEFINITION]: {
        pluralTitle: 'Missing definition',
        title: 'Missing definition'
      }
    },
    account_roles: {
      [EAccountRole.ADMIN]: {
        title: 'System administrator',
        summary: 'Full access. Manage all resources, groups and settings.'
      },
      [EAccountRole.VIEW]: {
        title: 'User',
        summary: 'View shared resources. Create and edit own resources.'
      },
      [EAccountRole.ACCESS_HUB]: {
        title: 'Access hub',
        summary: 'API settings only.'
      }
    },
    dashboard_activity_periods: {
      ['THIS_WEEK']: { title: 'This week' },
      ['THIS_MONTH']: { title: 'This month' },
      ['THIS_YEAR']: { title: 'This year' },
      ['SINCE_START']: { title: 'Since start' }
    },
    snackbar_messages: {
      // Accounting
      account_level_created: 'Account level created',
      account_level_created_failed: 'Failed to create account level',
      account_level_updated: 'Account level updated',
      account_level_updated_failed: 'Failed to update account level',
      account_level_deleted: 'Account level deleted',
      account_level_deleted_failed: 'Failed to delete account level',

      // API keys
      api_key_created: 'API key created',
      api_key_created_failed: 'Failed to create API key',
      api_key_deleted: 'API key deleted',
      api_key_deleted_failed: 'Failed to delete API key',
      export_tables_assigned_to_api_key: 'Access updated',
      export_tables_assigned_to_api_key_failed: 'Failed to update access',

      // MCP tokens

      // Chart of accounts
      chart_of_accounts_created: 'Chart of accounts created',
      chart_of_accounts_created_failed: 'Failed to create chart of accounts',
      chart_of_accounts_updated: 'Chart of accounts updated',
      chart_of_accounts_updated_failed: 'Failed to update chart of accounts',
      chart_of_accounts_deleted: 'Chart of accounts deleted',
      chart_of_accounts_deleted_failed: 'Failed to delete chart of accounts',

      // Company groups (customers)
      company_group_created: 'Customer created',
      company_group_created_failed: 'Failed to create customer',
      company_group_deleted: 'Customer deleted',
      company_group_deleted_failed: 'Failed to delete customer',
      company_group_updated: 'Customer updated',
      company_group_updated_failed: 'Failed to update customer',
      ontology_rerun_started: 'Data update started',
      ontology_rerun_started_failed: 'Failed to start the data update',

      // Dashboards
      dashboard_created: 'Dashboard created',
      dashboard_created_failed: 'Failed to create dashboard',
      dashboard_duplicated: 'Dashboard duplicated',
      dashboard_duplicated_failed: 'Failed to duplicate dashboard',
      dashboard_updated: 'Dashboard updated',
      dashboard_updated_failed: 'Failed to update dashboard',
      dashboard_deleted: 'Dashboard deleted',
      dashboard_deleted_failed: 'Failed to delete dashboard',
      dashboard_restored: 'Dashboard restored',
      dashboard_restored_failed: 'Failed to restore dashboard',

      // Analysis types
      analysis_type_updated: 'Analysis type updated',
      analysis_type_updated_failed: 'Failed to update analysi type',

      // Analyses
      analysis_deleted: 'Analysis deleted',
      analysis_deleted_failed: 'Failed to delete analysis',
      analysis_rerun: 'The analysis has been run again.',
      analysis_rerun_failed:
        'The analysis could not be rerun. Try again in a moment.',
      analysis_rerun_cancelled:
        'The run is cancelled. The analysis is unchanged.',

      // Groups
      access_requested: 'Access request sent',
      access_requested_failed: 'Failed to send access request',
      group_created: 'Group created',
      group_created_failed: 'Failed to create group',
      group_deleted: 'Group deleted',
      group_deleted_failed: 'Failed to delete group',
      group_updated: 'Group updated',
      group_updated_failed: 'Failed to update group',
      you_joined_group: 'You joined the group',
      you_joined_group_failed: 'Failed to join the group',
      you_left_group: 'You left the group',
      you_left_group_failed: 'Failed to leave the group',

      // Entities
      columns_updated: 'Columns updated',
      columns_updated_failed: 'Failed to update columns',
      slice_created: 'Slice created',
      slice_created_failed: 'Failed to create slice',
      slice_deleted: 'Slice deleted',
      slice_deleted_failed: 'Failed to delete slice',
      slice_updated: 'Slice updated',
      slice_updated_failed: 'Failed to update slice',

      // Export Tables
      export_table_created: 'Table created',
      export_table_created_failed: 'Failed to create table',
      export_table_updated: 'Table updated',
      export_table_updated_failed: 'Failed to update table',
      export_table_deleted: 'Table deleted',
      export_table_deleted_failed: 'Failed to delete table',
      export_table_api_keys_assigned_success: 'Access updated',
      export_table_api_keys_assigned_failed: 'Failed to update access',
      export_table_column_created: 'Column added',
      export_table_column_created_failed: 'Failed to add column',
      export_table_column_updated: 'Column updated',
      export_table_column_updated_failed: 'Failed to update column',
      export_table_column_deleted: 'Column removed',
      export_table_column_deleted_failed: 'Failed to remove column',

      // Filters
      filter_created: 'Filter created',
      filter_created_failed: 'Failed to create filter',
      filter_updated: 'Filter updated',
      filter_updated_failed: 'Failed to update filter',
      filter_deleted: 'Filter deleted',
      filter_deleted_failed: 'Failed to delete filter',

      // Folders
      folder_created: 'Folder created',
      folder_created_failed: 'Failed to create folder',
      folder_updated: 'Folder updated',
      folder_updated_failed: 'Failed to update folder',
      folder_deleted: 'Folder deleted',
      folder_deleted_failed: 'Failed to delete folder',

      // Tags
      tag_created: 'Tag created',
      tag_created_failed: 'Failed to create tag',

      // KPI:s
      kpi_created: 'Key metric created',
      kpi_created_failed: 'Failed to create key metric',
      kpi_updated: 'Key metric updated',
      kpi_updated_failed: 'Failed to update key metric',
      kpi_deleted: 'Key metric deleted',
      kpi_deleted_failed: 'Failed to delete key metric',
      kpi_order_updated: 'Key metric order updated',
      kpi_order_updated_failed: 'Failed to update key metric order',

      // Metrics
      metric_created: 'Metric created',
      metric_created_failed: 'Failed to create metric',
      metric_updated: 'Metric updated',
      metric_updated_failed: 'Failed to update metric',
      metric_deleted: 'Metric deleted',
      metric_deleted_failed: 'Failed to delete metric',
      metric_definition_removed: 'Definition removed',
      metric_definition_removed_failed: 'Failed to remove definition',

      // Reports
      report_duplicated: 'Report duplicated',
      report_duplicated_failed: 'Failed to duplicate report',
      report_published: 'Report published',
      report_published_failed: 'Failed to publish report',
      report_unpublished: 'Report unpublished',
      report_unpublished_failed: 'Failed to unpublish report',
      report_edition_published: 'Edition published',
      report_edition_published_failed: 'Failed to publish edition',
      report_edition_unpublished: 'Edition unpublished',
      report_edition_unpublished_failed: 'Failed to unpublish edition',

      // Tabs (note enum spelling)
      tab_created: 'Tab created',
      tab_created_failed: 'Failed to create tab',
      tab_duplicated: 'Tab duplicated',
      tab_duplicated_failed: 'Failed to duplicate tab',
      tab_deleleted: 'Tab deleted',
      tab_deleleted_failed: 'Failed to delete tab',
      tab_moved: 'Tab moved',
      tab_moved_failed: 'Failed to move tab',

      // Tasks
      task_created: 'Task created',
      task_created_failed: 'Failed to create task',
      task_updated: 'Task updated',
      task_updated_failed: 'Failed to update task',
      task_deleted: 'Task deleted',
      task_deleted_failed: 'Failed to delete task',

      // Users
      user_created: 'User created',
      user_created_failed: 'Failed to create user',
      user_updated: 'User updated',
      user_updated_failed: 'Failed to update user',
      user_deleted: 'User deleted',
      user_deleted_failed: 'Failed to delete user',

      // Widgets
      widget_created: 'Widget created',
      widget_created_failed: 'Failed to create widget',
      widget_updated: 'Widget updated',
      widget_updated_failed: 'Failed to update widget',
      widget_deleted: 'Widget deleted',
      widget_deleted_failed: 'Failed to delete widget',

      // Settings
      settings_saved: 'Settings saved',
      settings_saved_failed: 'Failed to save settings',

      // Organizaitional units
      organizational_unit_created: 'Data access group created',
      organizational_unit_created_failed: 'Failed to create data access group',
      organizational_unit_updated: 'Data access group updated',
      organizational_unit_updated_failed: 'Failed to update data access group',
      organizational_unit_deleted: 'Data access group deleted',
      organizational_unit_deleted_failed: 'Failed to delete data access group',

      // AI chat
      chat_feedback_sent: 'Thanks for your feedback!',
      chat_feedback_sent_failed: 'Could not send your feedback',

      // General
      copied_to_clipboard: 'Copied to clipboard',
      copied_to_clipboard_failed: 'Failed to copy to clipboard',
      file_uploaded: 'File uploaded',
      file_uploaded_failed: 'Failed to upload file',
      file_exported: 'File exported',
      file_exported_failed: 'Failed to export file',
      file_download_failed: 'Failed to download file',
      request_sent: 'Request sent',
      request_sent_failed: 'Failed to send request',

      // Generic internal UI only
      success: 'Done',
      failed: 'Failed'
    },
    snackbar_link_texts: {
      [SnackbarLinkText.NavigateToDashboard]: 'To dashboard'
    },
    comparative_periods: {
      [DateFilterType.PERIOD]: {
        // PERIOD FILTERS
        [PeriodFilter.THIS_WEEK_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Previous week',
          [ComparativePeriod.LAST_YEAR]: 'Same period last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '7-day average'
        },
        [PeriodFilter.THIS_MONTH_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Previous month',
          [ComparativePeriod.LAST_YEAR]: 'Same period last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '1-month average'
        },
        [PeriodFilter.THIS_QUARTER_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Previous quarter',
          [ComparativePeriod.LAST_YEAR]: 'Same period last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.THIS_YEAR_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Previous year',
          [ComparativePeriod.LAST_YEAR]: 'Same period last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Yesterday',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '1-day average'
        },
        [PeriodFilter.YESTERDAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'The day before yesterday',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '1-day average'
        },
        [PeriodFilter.LAST_WEEK]: {
          [ComparativePeriod.LAST_PERIOD]: 'Previous week',
          [ComparativePeriod.LAST_YEAR]: 'Same period last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '7-day average'
        },
        [PeriodFilter.LAST_MONTH]: {
          [ComparativePeriod.LAST_PERIOD]: 'Previous month',
          [ComparativePeriod.LAST_YEAR]: 'Same period last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '30-day average'
        },
        [PeriodFilter.LAST_QUARTER]: {
          [ComparativePeriod.LAST_PERIOD]: 'Previous quarter',
          [ComparativePeriod.LAST_YEAR]: 'Same period last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_YEAR]: {
          [ComparativePeriod.LAST_PERIOD]: 'Previous year',
          [ComparativePeriod.LAST_YEAR]: 'Same period last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_7_DAYS]: {
          [ComparativePeriod.LAST_PERIOD]: 'Previous 7 days',
          [ComparativePeriod.LAST_YEAR]: 'Same period last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '7-day average'
        },
        [PeriodFilter.LAST_30_DAYS]: {
          [ComparativePeriod.LAST_PERIOD]: 'Previous 30 days',
          [ComparativePeriod.LAST_YEAR]: 'Same period last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '30-day average'
        },
        [PeriodFilter.LAST_90_DAYS]: {
          [ComparativePeriod.LAST_PERIOD]: 'Previous 90 days',
          [ComparativePeriod.LAST_YEAR]: 'Same period last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_365_DAYS]: {
          [ComparativePeriod.LAST_PERIOD]: 'Previous 365 days',
          [ComparativePeriod.LAST_YEAR]: 'Same period last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LATEST_MONTH_WITH_DATA]: {
          [ComparativePeriod.LAST_PERIOD]: 'Previous month',
          [ComparativePeriod.LAST_YEAR]: 'Same period last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_3_MONTHS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Previous 3 months',
          [ComparativePeriod.LAST_YEAR]: 'Same period last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_6_MONTHS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Previous 6 months',
          [ComparativePeriod.LAST_YEAR]: 'Same period last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_12_MONTHS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Previous 12 months',
          [ComparativePeriod.LAST_YEAR]: 'Same period last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_4_QUARTERS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Previous 4 quarters',
          [ComparativePeriod.LAST_YEAR]: 'Same period last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_24_MONTHS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Previous 24 months',
          [ComparativePeriod.LAST_YEAR]: 'Same period last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_36_MONTHS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Previous 36 months',
          [ComparativePeriod.LAST_YEAR]: 'Same period last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_2_YEARS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Previous 2 years',
          [ComparativePeriod.LAST_YEAR]: 'Same period last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_3_YEARS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Previous 3 years',
          [ComparativePeriod.LAST_YEAR]: 'Same period last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_8_QUARTERS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Previous 8 quarters',
          [ComparativePeriod.LAST_YEAR]: 'Same period last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_12_QUARTERS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Previous 12 quarters',
          [ComparativePeriod.LAST_YEAR]: 'Same period last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.CURRENT_FINANCIAL_YEAR]: {
          [ComparativePeriod.LAST_PERIOD]: 'Same period last year',
          [ComparativePeriod.LAST_YEAR]: 'Same period last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.PREVIOUS_FINANCIAL_YEAR]: {
          [ComparativePeriod.LAST_PERIOD]: 'Same period last year',
          [ComparativePeriod.LAST_YEAR]: 'Same period last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_3_FINANCIAL_YEARS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: '-',
          [ComparativePeriod.LAST_YEAR]: '-',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        }
      },
      [DateFilterType.POINT]: {
        // POINTS
        [PeriodFilter.THIS_WEEK_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Same day previous week',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.THIS_MONTH_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Same day previous month',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.THIS_QUARTER_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Same day previous quarter',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.THIS_YEAR_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Same day last year',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Yesterday',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.YESTERDAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'The day before yesterday',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_WEEK]: {
          [ComparativePeriod.LAST_PERIOD]: 'Same day previous week',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_MONTH]: {
          [ComparativePeriod.LAST_PERIOD]: 'Same day previous month',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_QUARTER]: {
          [ComparativePeriod.LAST_PERIOD]: 'Same day previous quarter',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_YEAR]: {
          [ComparativePeriod.LAST_PERIOD]: '-',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_7_DAYS]: {
          [ComparativePeriod.LAST_PERIOD]: '7 days earlier',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_30_DAYS]: {
          [ComparativePeriod.LAST_PERIOD]: '30 days earlier',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_90_DAYS]: {
          [ComparativePeriod.LAST_PERIOD]: '90 days earlier',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_365_DAYS]: {
          [ComparativePeriod.LAST_PERIOD]: '365 days earlier',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LATEST_MONTH_WITH_DATA]: {
          [ComparativePeriod.LAST_PERIOD]: 'Same day previous month',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_3_MONTHS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Same day 3 months earlier',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_6_MONTHS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Same day 6 months earlier',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_12_MONTHS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Same day 12 months earlier',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_4_QUARTERS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Same day 4 quarters earlier',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_24_MONTHS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Same day 24 months earlier',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_36_MONTHS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Same day 36 months earlier',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_2_YEARS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Same day 2 years earlier',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_3_YEARS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Same day 3 years earlier',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_8_QUARTERS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Same day 8 quarters earlier',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_12_QUARTERS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: 'Same day 12 quarters earlier',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.CURRENT_FINANCIAL_YEAR]: {
          [ComparativePeriod.LAST_PERIOD]: 'Same day last year',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.PREVIOUS_FINANCIAL_YEAR]: {
          [ComparativePeriod.LAST_PERIOD]: 'Same day last year',
          [ComparativePeriod.LAST_YEAR]: 'Same day last year',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        },
        [PeriodFilter.LAST_3_FINANCIAL_YEARS_TO_TODAY]: {
          [ComparativePeriod.LAST_PERIOD]: '-',
          [ComparativePeriod.LAST_YEAR]: '-',
          [ComparativePeriod.AVERAGE_LAST_180_DAYS]: '-'
        }
      }
    }
  }
} satisfies Record<
  'sv' | 'en',
  {
    priority: Record<EPriority, { iconName: IconName; title: string }>
    period_filters: Record<PeriodFilter, { short: string; title: string }>
    conditions: Record<Condition, string>
    key_figure_visualization_types: Record<KeyFigureVisualizationType, string>
    pie_chart_value_visualization_types: Record<
      PieChartValueVisualizationType,
      string
    >
    text_aligns: Record<TextAlign, string>
    text_vertical_aligns: Record<TextVerticalAlign, string>
    categories: Record<Category, string>
    comparative_display_types: Record<
      ComparativeDisplayType,
      { title: string; tooltip: string }
    >
    widget_types: Record<
      WidgetType,
      { title: string; disabledTitle: string; summary: string }
    >
    rolling_types: Record<
      RollingType,
      { abbreviation: string; helperText: string; title: string }
    >
    breakdown_by_display_types: Record<
      BreakdownByDisplayType,
      { title: string }
    >
    api_key_types: Record<ApiKeyType, { title: string; body: string }>
    metric_statuses: Record<
      MetricStatus,
      { title: string; pluralTitle: string }
    >
    account_roles: Record<
      EAccountRole,
      { title: string; summary: string | ReactNode }
    >
    group_by_types: Record<'SEGMENT_BY' | 'BREAKDOWN_BY', { title: string }>
    show_pers: Record<
      ShowPerEnum,
      { abbreviation: string; helperText: string; title: string }
    >
    general_config_attributes: Record<GeneralConfigAttribute, { title: string }>
    period_types: Record<PeriodType, string>
    flaw_types: Record<FlawType, { title: string }>
    forecast_periods: Record<ForecastPeriod, { title: string }>
    report_status: Record<EPublicationStatus, string>
    analysis_status: Record<EAnalysisStatus, string>
    source_types: Record<TTaskSourceType, string>
    analysis_type_status: Record<
      TAnalysisTypeStatus,
      {
        color: Color | Severity | undefined
        pluralTitle: string
        title: string
      }
    >
    task_status: Record<
      TTaskStatus,
      { color: Color | Severity | undefined; iconName: IconName; title: string }
    >
    dashboard_activity_periods: Record<
      TDashboardActivityPeriod,
      { title: string }
    >
    snackbar_messages: Record<SnackbarMessage, string>
    snackbar_link_texts: Record<SnackbarLinkText, string>
    comparative_periods: Record<
      DateFilterType,
      Record<PeriodFilter, Record<ComparativePeriod, string>>
    >
  }
>
